// ============================================================================
// MarketGeoFields — ЕДИНЫЙ ввод гео-зоны/места (страна/регион/город/район,
// координаты, радиус) для ВСЕХ экранов маркетплейса.
//
// Используется мастером заявки (клиент), кабинетом мастера (зона обслуживания)
// и отборами ленты — одна разметка, одни правила и один источник справочников.
// Ссылки — существующие справочники ядра (countries/regions/cities/areas),
// каскад по владельцу (owner_id). Координаты можно ввести вручную или снять
// геолокацией (captureLocation) — подставляются в поля, ничего не выдумывается.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Crosshair, MapPin, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { captureLocation } from '@/lib/geolocation';
import {
  EMPTY_MARKET_GEO,
  loadMarketGeoOptions,
  logMarketGeoError,
  marketGeoFailureText,
  marketGeoHasValue,
  type MarketGeoOption,
  type MarketGeoValue,
} from '@/lib/marketGeo';

interface MarketGeoFieldsProps {
  value: MarketGeoValue;
  onChange: (value: MarketGeoValue) => void;
  /** Показывать радиус (у заявки — поиск, у мастера — обслуживание). */
  showRadius?: boolean;
  radiusLabel?: string;
  radiusHint?: string;
  /** Компактный вид (панель отборов ленты). */
  dense?: boolean;
  disabled?: boolean;
}

/** Загруженный список уровня + родитель, для которого он получен. */
interface GeoLevelData {
  parentId: string;
  options: MarketGeoOption[];
  error: string;
}

const EMPTY_LEVEL: GeoLevelData = { parentId: '', options: [], error: '' };

/** Города и районы одного региона (грузятся вместе). */
interface GeoChildData {
  parentId: string;
  cities: MarketGeoOption[];
  areas: MarketGeoOption[];
  cityError: string;
  areaError: string;
}

const EMPTY_CHILDREN: GeoChildData = {
  parentId: '',
  cities: [],
  areas: [],
  cityError: '',
  areaError: '',
};

/** Значение выбранной ссылки: если варианта нет в справочнике — явная метка. */
function optionsWithSelected(
  options: MarketGeoOption[],
  id: string,
): Array<{ id: string; name: string }> {
  if (!id) return options;
  if (options.some((o) => o.id === id)) return options;
  return [{ id, name: 'Текущий выбор' }, ...options];
}

export function MarketGeoFields({
  value,
  onChange,
  showRadius = true,
  radiusLabel = 'Радиус поиска, км',
  radiusHint,
  dense = false,
  disabled = false,
}: MarketGeoFieldsProps) {
  // Данные уровней хранят РОДИТЕЛЯ, для которого загружены: пока ответа нет,
  // список выводится пустым и «загружается» — без синхронного setState в
  // эффекте (state меняется только в асинхронных колбэках загрузки).
  const [countries, setCountries] = useState<GeoLevelData>(EMPTY_LEVEL);
  const [regions, setRegions] = useState<GeoLevelData>(EMPTY_LEVEL);
  const [children, setChildren] = useState<GeoChildData>(EMPTY_CHILDREN);
  const [locating, setLocating] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');
  const [locationError, setLocationError] = useState('');
  // Защита от гонок: ответ старого родителя не должен затирать новый список.
  const regionRequestRef = useRef(0);
  const childRequestRef = useRef(0);

  // Страны — один раз при открытии (общий справочник, важна работа офлайн из кэша).
  useEffect(() => {
    let cancelled = false;
    void loadMarketGeoOptions('country')
      .then((list) => {
        if (cancelled) return;
        setCountries({ parentId: 'root', options: list, error: '' });
      })
      .catch((e) => {
        if (cancelled) return;
        logMarketGeoError(e, 'Гео: страны');
        setCountries({
          parentId: 'root',
          options: [],
          error: marketGeoFailureText('country'),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Регионы выбранной страны.
  useEffect(() => {
    const countryId = value.countryId.trim();
    const request = ++regionRequestRef.current;
    if (!countryId) return;
    void loadMarketGeoOptions('region', countryId)
      .then((list) => {
        if (request !== regionRequestRef.current) return;
        setRegions({ parentId: countryId, options: list, error: '' });
      })
      .catch((e) => {
        if (request !== regionRequestRef.current) return;
        logMarketGeoError(e, 'Гео: регионы');
        setRegions({
          parentId: countryId,
          options: [],
          error: marketGeoFailureText('region'),
        });
      });
  }, [value.countryId]);

  // Города и районы выбранного региона (параллельно, независимо: отсутствие
  // одного справочника не скрывает другой).
  useEffect(() => {
    const regionId = value.regionId.trim();
    const request = ++childRequestRef.current;
    if (!regionId) return;
    void Promise.allSettled([
      loadMarketGeoOptions('city', regionId),
      loadMarketGeoOptions('area', regionId),
    ]).then(([citiesResult, areasResult]) => {
      if (request !== childRequestRef.current) return;
      if (citiesResult.status === 'rejected') {
        logMarketGeoError(citiesResult.reason, 'Гео: города');
      }
      if (areasResult.status === 'rejected') {
        logMarketGeoError(areasResult.reason, 'Гео: районы');
      }
      setChildren({
        parentId: regionId,
        cities: citiesResult.status === 'fulfilled' ? citiesResult.value : [],
        areas: areasResult.status === 'fulfilled' ? areasResult.value : [],
        cityError: citiesResult.status === 'rejected' ? marketGeoFailureText('city') : '',
        areaError: areasResult.status === 'rejected' ? marketGeoFailureText('area') : '',
      });
    });
  }, [value.regionId]);

  const patch = (next: Partial<MarketGeoValue>) => {
    onChange({ ...value, ...next });
  };

  const onCountryChange = (countryId: string) => {
    // Смена страны обнуляет нижестоящие уровни — иерархия не может «поехать».
    patch({ countryId, regionId: '', cityId: '', areaId: '' });
  };

  const onRegionChange = (regionId: string) => {
    patch({ regionId, cityId: '', areaId: '' });
  };

  const locate = async () => {
    setLocating(true);
    setLocationMessage('');
    setLocationError('');
    try {
      const point = await captureLocation();
      patch({
        lat: point.lat.toFixed(6),
        lon: point.lon.toFixed(6),
      });
      setLocationMessage(
        point.accuracy > 0
          ? `Координаты получены (точность ~${Math.round(point.accuracy)} м)`
          : 'Координаты получены',
      );
    } catch (e) {
      setLocationError(e instanceof Error ? e.message : 'Не удалось определить координаты');
    } finally {
      setLocating(false);
    }
  };

  const reset = () => {
    onChange({ ...EMPTY_MARKET_GEO });
    setLocationMessage('');
    setLocationError('');
  };

  const selectClass = dense
    ? 'h-9 w-full rounded-xl border bg-card px-2 text-xs outline-none focus:border-primary disabled:opacity-60'
    : 'h-11 w-full rounded-2xl border bg-background px-3 text-sm outline-none transition-colors focus:border-primary disabled:opacity-60';
  const labelClass = dense
    ? 'mb-1 block text-[10px] font-medium text-muted-foreground'
    : 'mb-1 block text-xs font-medium text-muted-foreground';
  const inputClass = dense ? 'h-9 rounded-xl text-xs' : 'h-11 rounded-2xl text-sm';

  // Состояние уровней выводится ИЗ загруженных данных и текущего значения:
  // если родитель сменился, а ответ ещё не пришёл — «загрузка» и пустой список.
  const countryLoading = countries.parentId !== 'root';
  const countryOptions = optionsWithSelected(countries.options, value.countryId);
  const countryError = countries.error;

  const regionParent = value.countryId.trim();
  const regionReady = !!regionParent && regions.parentId === regionParent;
  const regionLoading = !!regionParent && !regionReady;
  const regionOptions = regionReady
    ? optionsWithSelected(regions.options, value.regionId)
    : [];
  const regionError = regionReady ? regions.error : '';

  const childParent = value.regionId.trim();
  const childrenReady = !!childParent && children.parentId === childParent;
  const cityLoading = !!childParent && !childrenReady;
  const areaLoading = cityLoading;
  const cityOptions = childrenReady
    ? optionsWithSelected(children.cities, value.cityId)
    : [];
  const areaOptions = childrenReady
    ? optionsWithSelected(children.areas, value.areaId)
    : [];
  const cityError = childrenReady ? children.cityError : '';
  const areaError = childrenReady ? children.areaError : '';

  return (
    <div className={dense ? 'space-y-2' : 'space-y-3'}>
      {!dense && (
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <div className="text-sm font-medium">Место работы</div>
            <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
              Страна, регион, город и район берутся из справочников базы. Точные координаты
              нужны для подбора по расстоянию.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label>
          <span className={labelClass}>Страна</span>
          <select
            className={selectClass}
            value={value.countryId}
            onChange={(e) => onCountryChange(e.target.value)}
            disabled={disabled || countryLoading}
          >
            <option value="">{countryLoading ? 'Загрузка…' : 'Не указана'}</option>
            {countryOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
          {countryError && <div className="mt-1 text-[10px] text-destructive">{countryError}</div>}
        </label>

        <label>
          <span className={labelClass}>Регион</span>
          <select
            className={selectClass}
            value={value.regionId}
            onChange={(e) => onRegionChange(e.target.value)}
            disabled={disabled || !value.countryId || regionLoading}
          >
            <option value="">
              {!value.countryId ? 'Сначала выберите страну' : regionLoading ? 'Загрузка…' : 'Не указан'}
            </option>
            {regionOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
          {regionError && <div className="mt-1 text-[10px] text-destructive">{regionError}</div>}
        </label>

        <label>
          <span className={labelClass}>Город</span>
          <select
            className={selectClass}
            value={value.cityId}
            onChange={(e) => patch({ cityId: e.target.value })}
            disabled={disabled || !value.regionId || cityLoading}
          >
            <option value="">
              {!value.regionId ? 'Сначала выберите регион' : cityLoading ? 'Загрузка…' : 'Не указан'}
            </option>
            {cityOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
          {cityError && <div className="mt-1 text-[10px] text-destructive">{cityError}</div>}
        </label>

        <label>
          <span className={labelClass}>Район</span>
          <select
            className={selectClass}
            value={value.areaId}
            onChange={(e) => patch({ areaId: e.target.value })}
            disabled={disabled || !value.regionId || areaLoading}
          >
            <option value="">
              {!value.regionId ? 'Сначала выберите регион' : areaLoading ? 'Загрузка…' : 'Не указан'}
            </option>
            {areaOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
          {areaError && <div className="mt-1 text-[10px] text-destructive">{areaError}</div>}
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label>
          <span className={labelClass}>Широта</span>
          <Input
            type="number"
            inputMode="decimal"
            step="any"
            min={-90}
            max={90}
            className={inputClass}
            value={value.lat}
            onChange={(e) => patch({ lat: e.target.value })}
            placeholder="55.755800"
            disabled={disabled}
          />
        </label>
        <label>
          <span className={labelClass}>Долгота</span>
          <Input
            type="number"
            inputMode="decimal"
            step="any"
            min={-180}
            max={180}
            className={inputClass}
            value={value.lon}
            onChange={(e) => patch({ lon: e.target.value })}
            placeholder="37.617300"
            disabled={disabled}
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={dense ? 'h-8 rounded-lg text-[11px]' : 'h-9 rounded-xl text-xs'}
          onClick={() => void locate()}
          disabled={disabled || locating}
        >
          <Crosshair className="mr-1.5 h-3.5 w-3.5" />
          {locating ? 'Определяем…' : 'Определить координаты'}
        </Button>
        {marketGeoHasValue(value) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={dense ? 'h-8 rounded-lg text-[11px]' : 'h-9 rounded-xl text-xs'}
            onClick={reset}
            disabled={disabled}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Сбросить
          </Button>
        )}
      </div>

      {locationMessage && (
        <div className="text-[11px] leading-4 text-muted-foreground">{locationMessage}</div>
      )}
      {locationError && (
        <div className="flex items-start gap-1.5 text-[11px] leading-4 text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{locationError}</span>
        </div>
      )}

      {showRadius && (
        <label className="block">
          <span className={labelClass}>{radiusLabel}</span>
          <Input
            type="number"
            inputMode="decimal"
            step="1"
            min={0}
            className={dense ? 'h-9 w-full rounded-xl text-xs' : 'h-11 w-full rounded-2xl text-sm'}
            value={value.radiusKm}
            onChange={(e) => patch({ radiusKm: e.target.value })}
            placeholder="Например, 15"
            disabled={disabled}
          />
          {radiusHint && (
            <span className="mt-1 block text-[10px] leading-4 text-muted-foreground">{radiusHint}</span>
          )}
        </label>
      )}
    </div>
  );
}
